import { classNames } from "@/utils/classnames";
import React from "react";

export type SkeletonAnimation = "none" | "pulse";
export type SkeletonRadius = "none" | "sm" | "md" | "full";
export type SkeletonShape = "block" | "circle" | "text";
export type SkeletonTone = "default" | "subtle";

export interface SkeletonProps extends Omit<React.ComponentProps<"span">, "children"> {
  animation?: SkeletonAnimation;
  ariaHidden?: boolean;
  radius?: SkeletonRadius;
  shape?: SkeletonShape;
  tone?: SkeletonTone;
}

const radiusClassName: Record<SkeletonRadius, string> = {
  none: "rounded-none",
  sm: "rounded-cds-4",
  md: "rounded-cds-6",
  full: "rounded-full",
};

const shapeClassName: Record<SkeletonShape, string> = {
  block: "block",
  circle: "block aspect-square rounded-full",
  text: "inline-block h-[0.875em] align-middle",
};

const toneClassName: Record<SkeletonTone, string> = {
  default:
    "border border-cds-surface-border bg-cds-surface-active dark:border-0 dark:bg-white/[0.09]",
  subtle:
    "border border-cds-surface-border bg-cds-surface-hover/60 dark:border-0 dark:bg-white/[0.05]",
};

export const Skeleton: React.FC<SkeletonProps> = ({
  animation = "pulse",
  ariaHidden = true,
  className,
  radius = "md",
  shape = "block",
  tone = "default",
  ...rest
}) => {
  const bar = (
    <span
      aria-hidden={ariaHidden}
      className={classNames(
        "min-w-0 shrink-0 motion-reduce:animate-none",
        animation === "pulse" && "animate-pulse",
        shapeClassName[shape],
        shape !== "circle" && radiusClassName[radius],
        toneClassName[tone],
        className,
      )}
      {...rest}
    />
  );

  if (shape === "text") {
    // A bare inline-block bar loses the surrounding font's line box when it is a
    // flex/grid item or a wrapper's only child, rendering shorter than the text it
    // stands in for. The zero-width space gives the line real font metrics, so the
    // skeleton occupies exactly the height loaded text would.
    return (
      <span aria-hidden={ariaHidden} className="max-w-full">
        {"\u200B"}
        {bar}
      </span>
    );
  }

  return bar;
};

export interface SkeletonTextProps extends Omit<React.ComponentProps<"div">, "children"> {
  animation?: SkeletonAnimation;
  lineClassName?: string;
  lines?: number;
  tone?: SkeletonTone;
  widths?: readonly string[];
}

const getSkeletonTextWidth = (index: number, lines: number, widths: readonly string[]) => {
  if (widths[index] != null) {
    return widths[index];
  }

  return index === lines - 1 ? "w-2/3" : "w-full";
};

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  animation,
  className,
  lineClassName,
  lines = 1,
  tone,
  widths = [],
  ...rest
}) => {
  const lineCount = Math.max(1, lines);

  return (
    <div aria-hidden className={classNames("flex flex-col gap-2", className)} {...rest}>
      {Array.from({ length: lineCount }, (_, index) => (
        <Skeleton
          animation={animation}
          className={classNames(
            "h-3",
            getSkeletonTextWidth(index, lineCount, widths),
            lineClassName,
          )}
          key={index}
          radius="sm"
          shape="text"
          tone={tone}
        />
      ))}
    </div>
  );
};
